import axios from "axios";
import api from "./axios";

/**
 * PDF → question bank import.
 *
 * Upload is a two-step handshake: the backend hands out a presigned PUT and the browser sends the
 * bytes straight to object storage. The API itself never receives the PDF — Spring multipart is
 * capped at 5MB app-wide, and scanned exam papers are routinely several times that.
 */

export const createUploadUrl = async ({ filename, contentType, sizeBytes }) => {
    const res = await api.post("/question-imports/upload-url", {
        filename,
        contentType,
        sizeBytes,
    });
    return res.data; // { objectKey, url }
};

/**
 * PUT the file straight to storage. Uses a bare axios instance, not the app's `api` client: the
 * presigned URL carries its own signature and must NOT receive our Authorization header, which
 * would invalidate it.
 */
export const uploadToStorage = async (url, file, contentType, onProgress) => {
    await axios.put(url, file, {
        // MUST match the type the URL was signed for, or the PUT fails the signature check.
        headers: { "Content-Type": contentType },
        onUploadProgress: (e) => {
            if (onProgress && e.total) {
                onProgress(Math.round((e.loaded * 100) / e.total));
            }
        },
    });
};

export const registerImport = async ({ objectKey, originalFilename, subjectId, language }) => {
    const res = await api.post("/question-imports", {
        objectKey,
        originalFilename,
        subjectId,
        language,
    });
    return res.data;
};

/** One page of this tenant's imports → {content, totalElements, totalPages, pageNo}. */
export const getImportJobs = async ({ pageNo = 1, pageSize = 10 } = {}) => {
    const res = await api.get("/question-imports", { params: { pageNo, pageSize } });
    return res.data;
};

export const getImportJob = async (jobId) => {
    const res = await api.get(`/question-imports/${jobId}`);
    return res.data;
};

/**
 * One page of a job's staged questions.
 * → {content, totalElements, totalPages, pageNo, allCount, acceptedCount, rejectedCount,
 *    missingKeyCount, committedQuestionIds}
 *
 * The counts and committed ids are JOB-WIDE, not page-wide: the filter chips and the exam-template
 * action describe the whole import, so they can't be derived from the rows currently on screen.
 */
export const getImportedQuestions = async (jobId, { filter = "ALL", pageNo = 1, pageSize = 10 } = {}) => {
    const res = await api.get(`/question-imports/${jobId}/questions`, {
        params: { filter, pageNo, pageSize },
    });
    return res.data;
};

export const updateImportedQuestion = async (questionId, payload) => {
    const res = await api.put(`/question-imports/questions/${questionId}`, payload);
    return res.data;
};

/**
 * Set the correct option straight from the review list (no optionId clears it).
 * Supplying answer keys is the bulk of review, so it must be one click, not an edit round trip.
 */
export const setCorrectOption = async (questionId, optionId) => {
    const res = await api.post(`/question-imports/questions/${questionId}/answer`, null, {
        params: optionId ? { optionId } : undefined,
    });
    return res.data;
};

/**
 * Replace the picture on a staged question, or on one of its options.
 * Auto-cropping is usually right, but a teacher must always be able to substitute their own image.
 */
export const setQuestionImage = async (questionId, file, optionId) => {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post(`/question-imports/questions/${questionId}/image`, form, {
        params: optionId ? { optionId } : undefined,
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
};

/** Drop the picture from a staged question or one of its options. */
export const removeQuestionImage = async (questionId, optionId) => {
    const res = await api.delete(`/question-imports/questions/${questionId}/image`, {
        params: optionId ? { optionId } : undefined,
    });
    return res.data;
};

export const setImportedQuestionStatus = async (questionIds, status) => {
    const res = await api.post("/question-imports/questions/status", {
        questionIds,
        status,
    });
    return res.data;
};

/**
 * Assign one subject/topic to many staged questions. Pass questionIds for the checked rows, or
 * jobId to hit every non-committed question of the import — exactly one of the two.
 */
export const setImportedQuestionsSubject = async ({ questionIds, jobId, subjectId }) => {
    const res = await api.post("/question-imports/questions/subject", {
        questionIds: questionIds && questionIds.length ? questionIds : null,
        jobId: jobId ?? null,
        subjectId: Number(subjectId),
    });
    return res.data;
};

/** Delete a non-committed import job with its staged questions. Rejected for COMMITTED/PROCESSING. */
export const deleteImportJob = async (jobId) => {
    const res = await api.delete(`/question-imports/${Number(jobId)}`);
    return res.data;
};

export const commitImport = async (jobId) => {
    const res = await api.post(`/question-imports/${jobId}/commit`);
    return res.data;
};

/** Runs the whole upload handshake and returns the created job. */
export const uploadAndRegister = async ({ file, subjectId, language }, onProgress) => {
    // Browsers sometimes report nothing for .doc/.docx (and Windows reports several different
    // types); the server treats octet-stream as "unknown, verify by magic bytes after upload".
    const contentType = file.type || "application/octet-stream";
    const { objectKey, url } = await createUploadUrl({
        filename: file.name,
        contentType,
        sizeBytes: file.size,
    });
    await uploadToStorage(url, file, contentType, onProgress);
    return registerImport({
        objectKey,
        originalFilename: file.name,
        subjectId,
        language,
    });
};
